"use client"

import { useState, useRef, useCallback } from "react"
import { compressImage } from "@/lib/image-compress"

interface UseImageCompressReturn {
  /** 执行压缩，返回压缩后 File；取消时返回 null */
  compress: (file: File) => Promise<File | null>
  /** 取消当前压缩 */
  cancel: () => void
  /** 是否正在压缩 */
  compressing: boolean
  /** 压缩进度 0-100 */
  progress: number
}

/**
 * useImageCompress — 管理图片压缩生命周期
 *
 * 提供 compress / cancel / compressing / progress，
 * 配合 CompressProgressModal 使用。
 */
export function useImageCompress(): UseImageCompressReturn {
  const [compressing, setCompressing] = useState(false)
  const [progress, setProgress] = useState(0)
  const abortRef = useRef<AbortController | null>(null)

  const compress = useCallback(async (file: File): Promise<File | null> => {
    // 小图（≤ 1MB）不弹弹窗，直接返回
    if (file.size <= 1024 * 1024) {
      return file
    }

    const controller = new AbortController()
    abortRef.current = controller
    setCompressing(true)
    setProgress(0)

    try {
      const compressed = await compressImage(file, {
        signal: controller.signal,
        onProgress: setProgress,
      })
      return compressed
    } catch (err) {
      // AbortError = 用户主动取消
      if (err instanceof DOMException && err.name === "AbortError") {
        return null
      }
      // browser-image-compression 的 abort 抛出普通 Error
      if (err instanceof Error && err.message.includes("abort")) {
        return null
      }
      throw err
    } finally {
      setCompressing(false)
      setProgress(0)
      abortRef.current = null
    }
  }, [])

  const cancel = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  return { compress, cancel, compressing, progress }
}
